import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  normalizeTenantLocale,
  TenantLocaleSchema,
  type TenantLocale,
} from '@etiquetachef/types';

import ptBR from '@etiquetachef/i18n/pt-BR';
import ptPT from '@etiquetachef/i18n/pt-PT';
import en from '@etiquetachef/i18n/en';
import es from '@etiquetachef/i18n/es';
import esAR from '@etiquetachef/i18n/es-AR';
import esMX from '@etiquetachef/i18n/es-MX';
import esCL from '@etiquetachef/i18n/es-CL';
import esPY from '@etiquetachef/i18n/es-PY';
import it from '@etiquetachef/i18n/it';
import fr from '@etiquetachef/i18n/fr';
import de from '@etiquetachef/i18n/de';

// Locale segue o idioma do navegador. Variantes regionais (es-AR/MX/CL/PY,
// pt-PT) são respeitadas se o navegador entrega o BCP-47 completo;
// genéricos (`es`, `pt`, `de-AT`) caem no base.
//
// #86 (2026-10-13): a lista vem de `ALL_TENANT_LOCALES` (`@etiquetachef/types`)
// em vez de cópia à mão.
export type SupportedLocale = TenantLocale;

/**
 * Escolhe o locale inicial pelo idioma do navegador.
 *
 * Origem: sem issue — commit 88be39195, 2026-04-26 (Sprint 2).
 *
 * Contrato: BCP-47 exato suportado vence; senão cai no base pela raiz
 * (`normalizeTenantLocale`: `pt*` → pt-BR, `es*` → es, `de-AT` → de), e o
 * resto vai para `en`. Sem `navigator` (SSR/teste) devolve pt-BR.
 *
 * Usado por: o `i18n.init` deste módulo (importado por efeito em `main.tsx`).
 *
 * Dicas: as strings vêm de `packages/i18n/locales/*.json` (fonte da verdade
 * do web); chave nova entra nos locales lá, não aqui.
 *
 * Histórico:
 *  - 2026-10-13 11:05 +0200 — (#86) prefixos via `normalizeTenantLocale`; `de`.
 */
function detectInitialLocale(): SupportedLocale {
  if (typeof navigator === 'undefined') return 'pt-BR';
  const parsed = TenantLocaleSchema.safeParse(normalizeTenantLocale(navigator.language));
  return parsed.success ? parsed.data : 'en';
}

// `Record<SupportedLocale, …>`: locale novo no enum sem JSON aqui não compila.
const RESOURCES: Record<SupportedLocale, { translation: object }> = {
  'pt-BR': { translation: ptBR },
  'pt-PT': { translation: ptPT },
  en: { translation: en },
  es: { translation: es },
  'es-AR': { translation: esAR },
  'es-MX': { translation: esMX },
  'es-CL': { translation: esCL },
  'es-PY': { translation: esPY },
  it: { translation: it },
  fr: { translation: fr },
  de: { translation: de },
};

void i18n.use(initReactI18next).init({
  resources: RESOURCES,
  lng: detectInitialLocale(),
  fallbackLng: {
    'pt-PT': ['pt-BR'],
    'es-AR': ['es', 'pt-BR'],
    'es-MX': ['es', 'pt-BR'],
    'es-CL': ['es', 'pt-BR'],
    'es-PY': ['es', 'pt-BR'],
    es: ['pt-BR'],
    fr: ['pt-BR'],
    default: ['pt-BR'],
  },
  interpolation: { escapeValue: false },
  returnNull: false,
});

export { i18n };
